// Pill-style segmented tabs.
// Uses global `var(--md-...)` tokens defined in `styles/document-overlay-tokens.css`
// (loaded on `:root`/`.dark`, available app-wide).
//
// Originally lived in `components/document-overlay/primitives.tsx`; extracted
// here so it can be shared by the document overlay AND the investigation
// sidebar's inline entity-detail panel.

import type React from 'react'
import { cn } from '@/utils/cn'

export type SegmentedTab<V extends string = string> = {
  value: V
  label: string
  count?: number | string
  icon?: React.ReactNode
}

export function SegmentedTabs<V extends string = string>({
  tabs,
  active,
  onChange,
  size = 'md',
  flexWrap = false,
}: {
  tabs: SegmentedTab<V>[]
  active: V
  onChange?: (value: V) => void
  size?: 'sm' | 'md'
  flexWrap?: boolean
}) {
  const tabPadding = size === 'sm' ? '5px 11px' : '7px 14px'
  const fontSize = size === 'sm' ? 11 : 12
  const phoneTabHeight = size === 'sm' ? 'max-md:min-h-8' : 'max-md:min-h-9'

  return (
    // Below `md` the strip is always one swipeable line: wrapped tabs stack one per row
    // in a narrow slot.
    <div
      className={cn(
        'scrollbar-none max-md:max-w-full max-md:overflow-x-auto',
        flexWrap && 'md:flex-wrap'
      )}
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
      }}
    >
      {tabs.map(tab => {
        const isActive = tab.value === active
        return (
          <button
            key={tab.value}
            type="button"
            className={cn('max-md:shrink-0 max-md:whitespace-nowrap', phoneTabHeight)}
            onClick={() => onChange?.(tab.value)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: tabPadding,
              borderRadius: '8px',
              border: 'none',
              background: isActive ? 'var(--accent)' : 'transparent',
              color: isActive ? 'var(--foreground)' : 'var(--foreground)',
              fontSize,
              fontWeight: isActive ? 600 : 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: isActive ? 'inset 0 0 0 1px var(--border)' : 'none',
              transition: 'background 0.12s ease, color 0.12s ease',
            }}
          >
            {tab.icon && <span style={{ display: 'flex' }}>{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.count != null && (
              <span
                style={{
                  fontSize: 10.5,
                  padding: '1px 7px',
                  borderRadius: '9999px',
                  background: isActive ? 'var(--primary-surface)' : 'var(--accent)',
                  color: isActive ? 'var(--primary)' : 'var(--muted-foreground)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
